'use client';

import React, { useState } from 'react';
import styled from 'styled-components';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Breadcrumbs from '@/components/Breadcrumbs';
import FAQSection from '@/components/FAQSection';
import { guaranteeFaqs } from '@/data/faqData';
import { API_CONFIG, API_ENDPOINTS } from '@/constants/api';
import { COLORS } from '@/constants/colors';
import { TYPOGRAPHY, FONT_SIZE } from '@/constants/typography';
import { SPACING } from '@/constants/spacing';
import { BREAKPOINTS } from '@/constants/breakpoints';

const formFields = [
  { id: 0, name: 'firstName', type: 'text', label: 'First Name', required: true, placeholder: 'John' },
  { id: 1, name: 'lastName', type: 'text', label: 'Last Name', required: true, placeholder: 'Doe' },
  { id: 2, name: 'email', type: 'email', label: 'Email Address', required: true, placeholder: 'john.doe@example.com' },
  { id: 3, name: 'phone', type: 'tel', label: 'Phone', required: true, placeholder: '(555) 123-4567' },
  { id: 4, name: 'streetAddress', type: 'text', label: 'Street Address', required: true, placeholder: '123 Main Street' },
  { id: 5, name: 'apt', type: 'text', label: 'Apt/Suite', required: false, placeholder: 'Apt 4B' },
  { id: 6, name: 'city', type: 'text', label: 'City', required: true, placeholder: 'Los Angeles' },
  { id: 7, name: 'state', type: 'text', label: 'State', required: true, placeholder: 'California' },
  { id: 8, name: 'zip', type: 'text', label: 'Zip Code', required: true, placeholder: '90210' },
  { id: 9, name: 'partNumber', type: 'text', label: 'Part Number', required: true, placeholder: 'GF-1234' },
  { id: 10, name: 'purchaseDate', type: 'date', label: 'Purchase Date', required: true, placeholder: '' },
  { id: 11, name: 'purchaseLocation', type: 'text', label: 'Purchase Location', required: true, placeholder: 'Amazon, AutoZone, etc.' },
  { id: 12, name: 'vehicleYear', type: 'text', label: 'Vehicle Year', required: true, placeholder: '2022' },
  { id: 13, name: 'vehicleModel', type: 'text', label: 'Vehicle Model', required: true, placeholder: 'Ford F-150' },
];

interface FormErrors {
  [key: string]: string;
}

const brands = [
  { id: 0, imgSrc: '/assets/trust/brand0.webp', alt: 'Brand 0' },
  { id: 1, imgSrc: '/assets/trust/brand1.webp', alt: 'Brand 1' },
  { id: 2, imgSrc: '/assets/trust/brand2.webp', alt: 'Brand 2' },
  { id: 3, imgSrc: '/assets/trust/brand3.webp', alt: 'Brand 3' },
  { id: 4, imgSrc: '/assets/trust/brand4.webp', alt: 'Brand 4' },
  { id: 5, imgSrc: '/assets/trust/brand5.webp', alt: 'Brand 5' },
];

export default function OurGuaranteePage() {
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);
  const [submitMessage, setSubmitMessage] = useState('');

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Check required fields
    formFields.forEach((field) => {
      if (field.required && (!formData[field.name] || formData[field.name].trim() === '')) {
        newErrors[field.name] = `${field.label} is required`;
      }
    });

    // Email validation
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Zip validation (US format)
    if (formData.zip && !/^\d{5}(-\d{4})?$/.test(formData.zip)) {
      newErrors.zip = 'Please enter a valid zip code';
    }

    // Vehicle year validation
    if (formData.vehicleYear && !/^\d{4}$/.test(formData.vehicleYear)) {
      newErrors.vehicleYear = 'Please enter a valid 4-digit year';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);
    setSubmitMessage('');

    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_ENDPOINTS.warrantyRegistration}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitStatus('success');
        setSubmitMessage('Your warranty registration has been submitted successfully!');
        // Reset form
        setFormData({});
        setErrors({});
      } else {
        setSubmitStatus('error');

        // Handle validation errors from API
        if (data.errors && typeof data.errors === 'object') {
          const apiErrors: FormErrors = {};
          Object.entries(data.errors).forEach(([field, messages]) => {
            // API returns array of error messages per field
            if (Array.isArray(messages) && messages.length > 0) {
              apiErrors[field] = messages[0];
            } else if (typeof messages === 'string') {
              apiErrors[field] = messages;
            }
          });
          setErrors(apiErrors);
          setSubmitMessage('Please correct the errors below and try again.');
        } else {
          setSubmitMessage(data.message || 'Failed to submit warranty registration. Please try again.');
        }
      }
    } catch (error) {
      setSubmitStatus('error');
      setSubmitMessage('An error occurred. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Header handleClickIndex={() => {}} />
      <div className="header-gap-creator"></div>

      <PageContainer as="main">
        <Breadcrumbs items={[{ label: 'Why Green Filter' }, { label: 'Our Guarantee' }]} />
        <ContentWrapper>
          {/* Page Header */}
          <PageHeader>
            <PageTitle>We Stand By Our Filters</PageTitle>
            <PageSubtitle>
              All Green High Performance Air Filters are designed to last for the lifetime of your vehicle and are
              covered by our{' '}
              <a href="#warranty" className="green-link">
                Limited Lifetime Warranty
              </a>
              . Register your filter within 30 days of purchase.
            </PageSubtitle>
          </PageHeader>

          {/* Form Section */}
          <FormWrapper>
            <FormTitle>Warranty Registration</FormTitle>

            {submitStatus === 'success' && (
              <StatusMessage $variant="success">
                <StatusIcon $variant="success">✓</StatusIcon>
                <div>
                  <StatusTitle>Registration Successful!</StatusTitle>
                  <StatusText>{submitMessage}</StatusText>
                </div>
              </StatusMessage>
            )}

            {submitStatus === 'error' && (
              <StatusMessage $variant="error">
                <StatusIcon $variant="error">✕</StatusIcon>
                <div>
                  <StatusTitle>Registration Failed</StatusTitle>
                  <StatusText>{submitMessage}</StatusText>
                  {Object.keys(errors).length > 0 && (
                    <ErrorList>
                      {Object.entries(errors).map(([field, message]) => {
                        const fieldLabel = formFields.find((f) => f.name === field)?.label || field;
                        return message ? (
                          <li key={field}>
                            <strong>{fieldLabel}:</strong> {message}
                          </li>
                        ) : null;
                      })}
                    </ErrorList>
                  )}
                </div>
              </StatusMessage>
            )}

            <form onSubmit={handleSubmit}>
              {/* Personal Information */}
              <SectionLabel>Personal Information</SectionLabel>
              <FormGrid>
                {formFields.slice(0, 4).map((field) => (
                  <FormField key={field.id}>
                    <Label htmlFor={field.name}>
                      {field.label} {field.required && <Required>*</Required>}
                    </Label>
                    <Input
                      id={field.name}
                      type={field.type}
                      name={field.name}
                      value={formData[field.name] || ''}
                      onChange={handleChange}
                      $hasError={!!errors[field.name]}
                      placeholder={field.placeholder}
                      disabled={isSubmitting}
                    />
                    {errors[field.name] && <FieldError>{errors[field.name]}</FieldError>}
                  </FormField>
                ))}
              </FormGrid>

              {/* Address */}
              <SectionLabel>Address</SectionLabel>
              <FormGrid>
                {formFields.slice(4, 9).map((field) => (
                  <FormField key={field.id} $fullWidth={field.name === 'streetAddress'}>
                    <Label htmlFor={field.name}>
                      {field.label} {field.required && <Required>*</Required>}
                    </Label>
                    <Input
                      id={field.name}
                      type={field.type}
                      name={field.name}
                      value={formData[field.name] || ''}
                      onChange={handleChange}
                      $hasError={!!errors[field.name]}
                      placeholder={field.placeholder}
                      disabled={isSubmitting}
                    />
                    {errors[field.name] && <FieldError>{errors[field.name]}</FieldError>}
                  </FormField>
                ))}
              </FormGrid>

              {/* Purchase Information */}
              <SectionLabel>Purchase Information</SectionLabel>
              <FormGrid>
                {formFields.slice(9, 12).map((field) => (
                  <FormField key={field.id}>
                    <Label htmlFor={field.name}>
                      {field.label} {field.required && <Required>*</Required>}
                    </Label>
                    <Input
                      id={field.name}
                      type={field.type}
                      name={field.name}
                      value={formData[field.name] || ''}
                      onChange={handleChange}
                      $hasError={!!errors[field.name]}
                      placeholder={field.placeholder}
                      disabled={isSubmitting}
                    />
                    {errors[field.name] && <FieldError>{errors[field.name]}</FieldError>}
                  </FormField>
                ))}
              </FormGrid>

              {/* Vehicle Information */}
              <SectionLabel>Vehicle Information</SectionLabel>
              <FormGrid>
                {formFields.slice(12).map((field) => (
                  <FormField key={field.id}>
                    <Label htmlFor={field.name}>
                      {field.label} {field.required && <Required>*</Required>}
                    </Label>
                    <Input
                      id={field.name}
                      type={field.type}
                      name={field.name}
                      value={formData[field.name] || ''}
                      onChange={handleChange}
                      $hasError={!!errors[field.name]}
                      placeholder={field.placeholder}
                      disabled={isSubmitting}
                    />
                    {errors[field.name] && <FieldError>{errors[field.name]}</FieldError>}
                  </FormField>
                ))}
              </FormGrid>

              <SubmitButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Spinner />
                    Submitting...
                  </>
                ) : (
                  <>
                    <CheckIcon />
                    Register Warranty
                  </>
                )}
              </SubmitButton>
            </form>
          </FormWrapper>
        </ContentWrapper>

        {/* Section 2: Warranty Information */}
        <WarrantyInfoSection id="warranty">
          <ContentWrapper>
            <InfoCard>
              <InfoTitle>Warranty Information</InfoTitle>
              <InfoText>
                All Green High Performance Air Filters are designed to last for the lifetime of your vehicle and are
                covered by our Limited Lifetime Warranty.
              </InfoText>
              <InfoText>
                A warranty card is included with every Green High Performance Air Filter purchased, and you have 30 days
                from the date of purchase to return it. If this card was lost or if you did not receive one you can
                download it below.
              </InfoText>
              <InfoText>
                If you should need to make a warranty claim, please download the Warranty Return form below. The form
                provides instructions for returning a filter to us.
              </InfoText>
              <ButtonGrid>
                <DownloadButton href="/documents/gfu_warranty_return.pdf" target="_blank" rel="noopener noreferrer">
                  <DownloadIcon />
                  Warranty Return Form
                </DownloadButton>
                <DownloadButton href="/documents/gfu_warranty_card.pdf" target="_blank" rel="noopener noreferrer">
                  <DownloadIcon />
                  Warranty Card
                </DownloadButton>
              </ButtonGrid>
              <WarningText>
                * Never use motor oil, transmission fluid, WD-40®, or any other brand of filter oil as these may damage
                your Green Filter. Use only genuine Green Filter Oil.
              </WarningText>
            </InfoCard>

            <InfoCard>
              <InfoTitle>Return or Exchange a Filter</InfoTitle>
              <InfoText>
                If you need to return or exchange a filter, please download and complete the Filter Return form. Once
                you have completed the form, send it along with the filter to the address listed on the form.
              </InfoText>
              <WarningText $highlight>
                *NOTE: YOU MUST CALL US FOR AN RGA NUMBER BEFORE SENDING ANY FILTER TO US. Any filter sent to us without
                an RGA number may not be exchanged. Please read the form carefully and make sure that all steps are
                followed to ensure a quick refund or exchange of your filter.
              </WarningText>
              <InfoText>There is a 25% restocking fee on all returns for credit.</InfoText>
              <DownloadButton href="/documents/gfu_filter_credit_return.pdf" target="_blank" rel="noopener noreferrer">
                <DownloadIcon />
                Filter Return Form
              </DownloadButton>
            </InfoCard>
          </ContentWrapper>
        </WarrantyInfoSection>

        {/* Section 3: Video and Trusted Brands */}
        <VideoSection>
          <ContentWrapper>
            <VideoWrapper>
              <iframe
                width="100%"
                height="100%"
                src="https://www.youtube.com/embed/om0RyH2ZXr8"
                title="YouTube video player"
                frameBorder="0"
                allow="autoplay; encrypted-media;"
                allowFullScreen
              ></iframe>
            </VideoWrapper>
            <VideoTitle>Watch Green Filter Outperform the Competition</VideoTitle>
          </ContentWrapper>
        </VideoSection>

        <BrandsSection>
          <ContentWrapper>
            <BrandsTitle>THE CHOSEN AIR FILTER BY THE WORLD'S BEST</BrandsTitle>
            <BrandsGrid>
              {brands.map((brand) => (
                <BrandImage key={brand.id} src={brand.imgSrc} alt={brand.alt} />
              ))}
            </BrandsGrid>
          </ContentWrapper>
        </BrandsSection>

        {/* Section 4: FAQs */}
        <FAQSection faqs={guaranteeFaqs} title="FAQs About Green Filter Warranty and Returns" />
      </PageContainer>

      <Footer />
    </>
  );
}

// Styled Components
const PageContainer = styled.div`
  background: ${COLORS.white};
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${SPACING.xl} ${SPACING.lg};

  @media (max-width: ${BREAKPOINTS.mobile}) {
    padding: ${SPACING.lg} ${SPACING.md};
  }
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: ${SPACING.xl};

  .green-link {
    color: ${COLORS.brandGreen};
    text-decoration: none;
    font-weight: 600;

    &:hover {
      text-decoration: underline;
    }
  }

  @media (max-width: ${BREAKPOINTS.mobile}) {
    margin-bottom: ${SPACING.lg};
  }
`;

const PageTitle = styled.h1`
  ${TYPOGRAPHY.h1}
  color: ${COLORS.textPrimary};
  margin-top: 0;
  margin-bottom: ${SPACING.sm};

  @media (max-width: ${BREAKPOINTS.mobile}) {
    font-size: ${FONT_SIZE.heading.md};
    margin-bottom: ${SPACING.xs};
  }
`;

const PageSubtitle = styled.p`
  ${TYPOGRAPHY.body}
  color: ${COLORS.textSecondary};
  max-width: 800px;
  margin: 0 auto;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    font-size: ${FONT_SIZE.body.sm};
  }
`;

const FormWrapper = styled.div`
  background: ${COLORS.white};
  border-radius: 12px;
  padding: ${SPACING.xl};
  border: 1px solid ${COLORS.borderGray};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  max-width: 900px;
  margin: 0 auto;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    padding: ${SPACING.md};
  }
`;

const FormTitle = styled.h2`
  ${TYPOGRAPHY.h3}
  color: ${COLORS.textPrimary};
  margin-bottom: ${SPACING.lg};
  text-align: center;
`;

const SectionLabel = styled.div`
  ${TYPOGRAPHY.label}
  color: ${COLORS.brandGreen};
  margin-bottom: ${SPACING.sm};
  margin-top: ${SPACING.lg};
  text-transform: uppercase;
  font-size: ${FONT_SIZE.body.xs};
  letter-spacing: 1px;
  font-weight: 600;

  &:first-of-type {
    margin-top: 0;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${SPACING.md};

  @media (max-width: ${BREAKPOINTS.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const FormField = styled.div<{ $fullWidth?: boolean }>`
  ${(props) => props.$fullWidth && 'grid-column: 1 / -1;'}
`;

const Label = styled.label`
  ${TYPOGRAPHY.label}
  color: ${COLORS.textPrimary};
  display: block;
  margin-bottom: ${SPACING.xs};
  font-weight: 500;
`;

const Required = styled.span`
  color: ${COLORS.error};
`;

const Input = styled.input<{ $hasError?: boolean }>`
  ${TYPOGRAPHY.body}
  width: 100%;
  padding: ${SPACING.sm} ${SPACING.md};
  border: 1px solid ${(props) => (props.$hasError ? COLORS.error : COLORS.borderGray)};
  border-radius: 6px;
  background: ${COLORS.white};
  color: ${COLORS.textPrimary};
  transition: all 0.2s ease;

  &::placeholder {
    color: ${COLORS.textMuted};
  }

  &:focus {
    outline: none;
    border-color: ${(props) => (props.$hasError ? COLORS.error : COLORS.brandGreen)};
    box-shadow: 0 0 0 3px ${(props) => (props.$hasError ? 'rgba(244, 67, 54, 0.1)' : 'rgba(0, 173, 35, 0.1)')};
  }

  &:disabled {
    background: ${COLORS.bgGray};
    cursor: not-allowed;
  }
`;

const FieldError = styled.div`
  ${TYPOGRAPHY.caption}
  color: ${COLORS.error};
  margin-top: ${SPACING.xs};
  display: flex;
  align-items: center;
  gap: 4px;

  &::before {
    content: '⚠';
  }
`;

const StatusMessage = styled.div<{ $variant: 'success' | 'error' }>`
  background: linear-gradient(
    135deg,
    ${(props) => (props.$variant === 'success' ? COLORS.success : COLORS.error)} 0%,
    ${(props) => (props.$variant === 'success' ? COLORS.successDark : COLORS.errorDark)} 100%
  );
  color: ${COLORS.white};
  padding: ${SPACING.md} ${SPACING.lg};
  border-radius: 12px;
  margin-bottom: ${SPACING.lg};
  display: flex;
  align-items: flex-start;
  gap: ${SPACING.md};
  animation: slideIn 0.3s ease;

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: ${BREAKPOINTS.mobile}) {
    padding: ${SPACING.sm} ${SPACING.md};
    margin-bottom: ${SPACING.md};
    gap: ${SPACING.sm};
  }
`;

const StatusIcon = styled.div<{ $variant: 'success' | 'error' }>`
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${FONT_SIZE.heading.sm};
  font-weight: 700;
  flex-shrink: 0;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    width: 32px;
    height: 32px;
    font-size: ${FONT_SIZE.heading.xs};
  }
`;

const StatusTitle = styled.div`
  ${TYPOGRAPHY.h5}
  margin-bottom: ${SPACING.xs};

  @media (max-width: ${BREAKPOINTS.mobile}) {
    font-size: ${FONT_SIZE.body.md};
  }
`;

const StatusText = styled.div`
  ${TYPOGRAPHY.bodyXS}
  opacity: 0.95;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    font-size: ${FONT_SIZE.body.xxs};
  }
`;

const ErrorList = styled.ul`
  list-style: none;
  padding: 0;
  margin: ${SPACING.sm} 0 0 0;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  padding: ${SPACING.sm} ${SPACING.md};

  li {
    padding: ${SPACING.xs} 0;
    font-size: ${FONT_SIZE.body.sm};

    &:not(:last-child) {
      border-bottom: 1px solid rgba(255, 255, 255, 0.2);
    }

    strong {
      font-weight: 600;
    }
  }
`;

const SubmitButton = styled.button`
  ${TYPOGRAPHY.button}
  width: 100%;
  padding: ${SPACING.md} ${SPACING.xl};
  background: linear-gradient(135deg, ${COLORS.brandGreen} 0%, ${COLORS.successAlt} 100%);
  color: ${COLORS.white};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${SPACING.sm};
  margin-top: ${SPACING.xl};

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, ${COLORS.successAlt} 0%, ${COLORS.brandGreen} 100%);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 173, 35, 0.3);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    background: ${COLORS.gray600};
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

const Spinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: ${COLORS.white};
  border-radius: 50%;
  animation: spin 0.6s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

// Warranty Info Section
const WarrantyInfoSection = styled.section`
  background: ${COLORS.bgGray};
  padding: ${SPACING.xl} 0;
`;

const InfoCard = styled.div`
  background: ${COLORS.white};
  border-radius: 12px;
  padding: ${SPACING.xl};
  border: 1px solid ${COLORS.borderGray};
  margin-bottom: ${SPACING.lg};

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: ${BREAKPOINTS.mobile}) {
    padding: ${SPACING.md};
  }
`;

const InfoTitle = styled.h2`
  ${TYPOGRAPHY.h3}
  color: ${COLORS.textPrimary};
  margin-bottom: ${SPACING.md};
`;

const InfoText = styled.p`
  ${TYPOGRAPHY.body}
  color: ${COLORS.textSecondary};
  margin-bottom: ${SPACING.md};
  line-height: 1.7;

  &:last-of-type {
    margin-bottom: ${SPACING.lg};
  }
`;

const WarningText = styled.p<{ $highlight?: boolean }>`
  ${TYPOGRAPHY.bodyXS}
  color: ${(props) => (props.$highlight ? COLORS.error : COLORS.textMuted)};
  font-style: italic;
  margin-top: ${SPACING.md};
  padding: ${SPACING.sm};
  background: ${(props) => (props.$highlight ? 'rgba(244, 67, 54, 0.05)' : 'transparent')};
  border-radius: 4px;
`;

const ButtonGrid = styled.div`
  display: flex;
  gap: ${SPACING.md};
  margin-bottom: ${SPACING.md};

  @media (max-width: ${BREAKPOINTS.mobile}) {
    flex-direction: column;
  }
`;

const DownloadButton = styled.a`
  ${TYPOGRAPHY.button}
  display: inline-flex;
  align-items: center;
  gap: ${SPACING.sm};
  padding: ${SPACING.sm} ${SPACING.lg};
  background: linear-gradient(135deg, ${COLORS.brandGreen} 0%, ${COLORS.successAlt} 100%);
  color: ${COLORS.white};
  border-radius: 6px;
  text-decoration: none;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 173, 35, 0.3);
  }
`;

// Video Section
const VideoSection = styled.section`
  background: ${COLORS.white};
  padding: ${SPACING.xl} 0;
`;

const VideoWrapper = styled.div`
  position: relative;
  padding-bottom: 56.25%;
  height: 0;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
`;

const VideoTitle = styled.h2`
  ${TYPOGRAPHY.h3}
  color: ${COLORS.textPrimary};
  text-align: center;
  margin-top: ${SPACING.lg};
`;

// Brands Section
const BrandsSection = styled.section`
  background: ${COLORS.bgGray};
  padding: ${SPACING.xl} 0;
`;

const BrandsTitle = styled.h2`
  ${TYPOGRAPHY.h4}
  color: ${COLORS.textPrimary};
  text-align: center;
  margin-bottom: ${SPACING.lg};
  letter-spacing: 2px;
`;

const BrandsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: ${SPACING.md};
  align-items: center;

  @media (max-width: ${BREAKPOINTS.tablet}) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: ${BREAKPOINTS.mobile}) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const BrandImage = styled.img`
  width: 100%;
  max-width: 120px;
  margin: 0 auto;
  opacity: 0.8;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
  }
`;

// Icons
const CheckIcon = () => (
  <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const DownloadIcon = () => (
  <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
    />
  </svg>
);
